import React, {useCallback} from 'react';

import {Analytics} from '../../../../services/analytics';
import {ColorPalette} from '../colorPalette/ColorPalette';
import {RunColorOptions, SemanticLegendSettings, YAxisValues} from '../types';
import {SemanticLegendBucketsWithCategoricalConfig} from './SemanticLegendBucketsWithCategoricalConfig';
import {SemanticLegendCategories} from './SemanticLegendCategories';
import {DEFAULT_SEMANTIC_LEGEND_SETTINGS} from './SemanticLegendDefaults';
import {SemanticLegendExcludedRunColor} from './SemanticLegendExcludedRunColor';
import {SemanticLegendKeyWithCategoricalConfig} from './SemanticLegendKeyWithCategoricalConfig';
import {SemanticLegendNumBucketsWithCategoricalConfig} from './SemanticLegendNumBucketsWithCategoricalConfig';
import {SemanticLegendRunColorOptions} from './SemanticLegendRunColorOptions';
import {useSemanticLegendSettingsContext} from './SemanticLegendSettingsContextProvider';
import {SemanticLegendSettingsWithCategoricalConfigContextType} from './SemanticLegendSettingsWithCategoricalConfigContextProvider';
import {SemanticLegendYValueWithCategoricalConfig} from './SemanticLegendYValueWithCategoricalConfig';
import {useGetWorkspaceSettingsSemanticLegendPaletteWithCategoricalConfig} from './utils/useGetWorkspaceSettingsSemanticLegendPaletteWithCategoricalConfig';

export const SemanticLegendColorTabWithCategoricalConfig = () => {
  const {
    hasActiveGrouping,
    configKeys,
    semanticLegendSettings,
    semanticLegendRunColorOptions,
    isMetricBasedColors,
    updateSemanticLegendRunColorOptions,
    updateSemanticLegendSettings,
    isLoadingBuckets,
  } =
    useSemanticLegendSettingsContext() as SemanticLegendSettingsWithCategoricalConfigContextType; // We know this is this type because of the component fork

  const getSemanticLegendPalette =
    useGetWorkspaceSettingsSemanticLegendPaletteWithCategoricalConfig();

  const showMetricBasedColors = !hasActiveGrouping && isMetricBasedColors;
  const updateSetting = useCallback(
    (value: Partial<SemanticLegendSettings>) => {
      updateSemanticLegendSettings?.({
        ...semanticLegendSettings,
        ...value,
      });
    },
    [semanticLegendSettings, updateSemanticLegendSettings]
  );

  return (
    <div
      data-test="workspace-semantic-legend-color-settings"
      className={'flex h-full w-full flex-col gap-20 py-16'}>
      <SemanticLegendRunColorOptions
        hasActiveGrouping={hasActiveGrouping}
        runColorOptions={
          semanticLegendRunColorOptions ?? RunColorOptions.OriginalProjectColors
        }
        onRunColorOptionsChange={value => {
          Analytics.track('Run color options changed', {newValue: value});
          updateSemanticLegendRunColorOptions?.(value);
        }}
      />
      {semanticLegendRunColorOptions ===
        RunColorOptions.OriginalProjectColors && <ColorPalette />}
      {showMetricBasedColors && (
        <div className="ml-10 flex h-full w-full flex-col gap-10 border-l-2 border-moon-350">
          <SemanticLegendKeyWithCategoricalConfig
            currentKey={semanticLegendSettings?.key ?? ''}
            type={
              semanticLegendSettings?.yAxisValue ??
              DEFAULT_SEMANTIC_LEGEND_SETTINGS.yAxisValue
            }
            configKeys={configKeys}
            updateSetting={updateSetting}
          />
          {semanticLegendSettings.key !==
            DEFAULT_SEMANTIC_LEGEND_SETTINGS.key && (
            <SemanticLegendYValueWithCategoricalConfig
              yValue={semanticLegendSettings.yAxisValue}
              updateSetting={updateSetting}
            />
          )}
        </div>
      )}
      {showMetricBasedColors &&
        semanticLegendSettings.key !== DEFAULT_SEMANTIC_LEGEND_SETTINGS.key && (
          <>
            <div className="flex h-full w-full flex-col">
              <SemanticLegendNumBucketsWithCategoricalConfig
                numberOfBuckets={semanticLegendSettings.numberOfBuckets ?? 0}
                updateSetting={updateSetting}
              />

              {semanticLegendSettings?.yAxisValue ===
              YAxisValues.Config_Categories ? (
                <SemanticLegendCategories
                  allCategories={semanticLegendSettings.allCategories ?? []}
                  selectedCategories={
                    semanticLegendSettings.selectedCategories ?? []
                  }
                  categoryCustomizations={
                    semanticLegendSettings.bucketCustomizations
                  }
                  semanticLegendPalette={getSemanticLegendPalette(
                    semanticLegendSettings.yAxisValue
                  )}
                  updateSetting={updateSetting}
                  isLoadingBuckets={isLoadingBuckets}
                />
              ) : (
                <SemanticLegendBucketsWithCategoricalConfig
                  bucketCustomizations={
                    semanticLegendSettings?.bucketCustomizations ?? {}
                  }
                  buckets={semanticLegendSettings.buckets ?? []}
                  updateSetting={updateSetting}
                  isLoadingBuckets={isLoadingBuckets}
                />
              )}
              <SemanticLegendExcludedRunColor
                excludedRunColor={semanticLegendSettings.excludedRunColor ?? ''}
                updateSetting={updateSetting}
              />
            </div>
          </>
        )}
    </div>
  );
};
